/**
 * T4 prep — each employee's year totals laid out in T4 box order so
 * year-end filing is a copy job. SIN is masked until revealed; every row
 * has a one-tap copy (tab-separated, box order) and the whole table
 * exports as CSV.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Download, Eye, EyeOff, Loader2, Printer, PencilLine } from "lucide-react";
import api from "@/lib/api";
import { downloadBlob } from "./downloadBlob";
import MissingCpp2Dialog from "./MissingCpp2Dialog";

const money = (v) => Number(v || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const BOXES = [["14", "Employment income"], ["16", "CPP"], ["16A", "CPP2"], ["18", "EI premiums"],
  ["22", "Income tax"], ["24", "EI insurable"], ["26", "CPP pensionable"]];

export default function T4PrepView({ year }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSin, setShowSin] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [slipping, setSlipping] = useState(null); // employee_id | "all" while a slip PDF downloads
  const [cpp2For, setCpp2For] = useState(null);   // employee whose missing-CPP2 finder is open

  const slipPdf = async (x) => {
    setSlipping(x ? x.employee_id : "all");
    try {
      const url = x ? `/payroll/t4-prep/${x.employee_id}/slip` : "/payroll/t4-prep/slips";
      const r = await api.get(url, { params: { year }, responseType: "blob" });
      downloadBlob(r.data, x ? `T4_${year}_${x.last_name}_${x.first_name}.pdf` : `T4_slips_${year}.pdf`, r.headers["content-disposition"]);
      toast.success(x ? `T4 slip for ${x.name} ready — two copies on one page` : "All T4 slips ready — one page per employee");
    } catch {
      toast.error("Could not build the T4 slip PDF");
    } finally {
      setSlipping(null);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/payroll/t4-prep", { params: { year } });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load T4 prep");
    } finally {
      setLoading(false);
    }
  }, [year]);
  useEffect(() => { load(); }, [load]);

  const copyRow = async (x) => {
    const b = x.boxes;
    const line = [x.last_name, x.first_name, x.province, x.sin, ...BOXES.map(([k]) => Number(b[k] || 0).toFixed(2))].join("\t");
    try {
      await navigator.clipboard.writeText(line);
      toast.success(`${x.name} copied — last name, first name, province, SIN, then boxes ${BOXES.map(([k]) => k).join(", ")}`);
    } catch {
      toast.error("Clipboard blocked by the browser");
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const r = await api.get("/payroll/t4-prep/export", { params: { year }, responseType: "blob" });
      downloadBlob(r.data, `T4_prep_${year}.csv`, r.headers["content-disposition"]);
    } catch {
      toast.error("Could not export the T4 prep");
    } finally {
      setExporting(false);
    }
  };

  if (loading && !data) {
    return <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-white/50" data-testid="t4-loading"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</p>;
  }
  if (!data) return null;
  const lim = data.limits || {};
  const t = data.totals || {};

  return (
    <div className="space-y-3" data-testid="t4-prep-view">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-mono text-[10px] text-white/50">
          Box 14 = gross incl. vacation pay · 22 = federal + Ontario tax · 24 caps at the EI maximum (${money(lim.ei_mie)}) · 26 caps at the YAMPE (${money(lim.yampe)}). Employer CPP/EI never go on a T4.
        </p>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setShowSin((v) => !v)} data-testid="t4-toggle-sin"
            className="inline-flex items-center gap-1.5 rounded-md border border-white/15 bg-white/5 px-2.5 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-widest text-white/70 hover:border-[#1a3fd1] hover:text-white">
            {showSin ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />} {showSin ? "Hide SINs" : "Show SINs"}
          </button>
          <button type="button" onClick={() => slipPdf(null)} disabled={slipping === "all" || !data.employees.length} data-testid="t4-slips-all"
            title="Print every employee's T4-style slip — one page each, two copies per page"
            className="inline-flex items-center gap-1.5 rounded-md bg-[#1a3fd1] px-2.5 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-widest text-white hover:bg-[#1a3fd1]/85 disabled:opacity-50">
            {slipping === "all" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Printer className="h-3.5 w-3.5" />} All slips PDF
          </button>
          <button type="button" onClick={exportCsv} disabled={exporting} data-testid="t4-export-csv"
            className="inline-flex items-center gap-1.5 rounded-md border border-white/15 bg-white/5 px-2.5 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-widest text-white/70 hover:border-[#1a3fd1] hover:text-white disabled:opacity-50">
            {exporting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} CSV
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-white/10 bg-white/[0.02]" data-testid="t4-table">
        <table className="w-full min-w-[1100px] border-collapse text-xs">
          <thead>
            <tr className="font-mono text-[9px] uppercase tracking-widest text-white/50">
              <th className="px-3 py-2 text-left font-normal">Employee</th>
              <th className="px-2 py-2 text-left font-normal">Box 10<br />Prov</th>
              <th className="px-2 py-2 text-left font-normal">Box 12<br />SIN</th>
              {BOXES.map(([k, lb]) => <th key={k} className="px-2 py-2 text-right font-normal">Box {k}<br />{lb}</th>)}
              <th className="px-2 py-2 text-right font-normal"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/10">
            {data.employees.map((x) => (
              <tr key={x.employee_id} className="text-white/80 hover:bg-white/[0.03]" data-testid={`t4-row-${x.employee_id}`}>
                <td className="px-3 py-1.5">
                  <span className="font-semibold text-white">{x.last_name}, {x.first_name}</span>
                  {x.status !== "Active" && <span className="ml-1.5 rounded-full bg-white/15 px-1.5 py-0.5 font-mono text-[8px] font-bold uppercase tracking-wider text-white/80">{x.status}</span>}
                  {x.address && <p className="font-mono text-[10px] text-white/50">{x.address}</p>}
                  {x.notes.map((n) => <p key={n} className="font-mono text-[10px] text-amber-700 dark:text-[#fde68a]">{n}</p>)}
                  {x.imported_cpp2_missing > 0 && (
                    <button type="button" onClick={() => setCpp2For(x)} data-testid={`t4-cpp2-finder-${x.employee_id}`}
                      className="mt-1 inline-flex items-center gap-1 rounded-md border border-amber-500/60 bg-amber-500/10 px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider text-amber-700 hover:bg-amber-500/20 dark:text-[#fde68a]">
                      <PencilLine className="h-3 w-3" /> Enter CPP2 ({x.imported_cpp2_missing})
                    </button>
                  )}
                </td>
                <td className="px-2 py-1.5 font-mono">{x.province}</td>
                <td className="px-2 py-1.5 font-mono tabular-nums" data-testid={`t4-sin-${x.employee_id}`}>
                  {x.sin
                    ? (showSin ? x.sin.replace(/(\d{3})(\d{3})(\d{3})/, "$1 $2 $3") : "••• ••• " + x.sin.slice(-3))
                    : <span className="rounded bg-[#5a4208] px-1.5 py-0.5 text-[9px] font-bold uppercase text-[#fde68a]">missing</span>}
                  {x.sin && !x.sin_ok && <span className="ml-1 rounded bg-[#5a4208] px-1 py-0.5 text-[8px] font-bold uppercase text-[#fde68a]">check</span>}
                </td>
                {BOXES.map(([k]) => <td key={k} className="px-2 py-1.5 text-right font-mono tabular-nums">{money(x.boxes[k])}</td>)}
                <td className="whitespace-nowrap px-2 py-1.5 text-right">
                  <button type="button" onClick={() => slipPdf(x)} disabled={slipping === x.employee_id} data-testid={`t4-slip-${x.employee_id}`} title="Print this employee's T4-style slip (PDF, two copies)"
                    className="mr-1 inline-flex items-center gap-1 rounded border border-white/15 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider text-white/70 hover:border-[#1a3fd1] hover:text-white disabled:opacity-50">
                    {slipping === x.employee_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Printer className="h-3 w-3" />} slip
                  </button>
                  <button type="button" onClick={() => copyRow(x)} data-testid={`t4-copy-${x.employee_id}`} title="Copy this row (tab-separated, box order)"
                    className="inline-flex items-center gap-1 rounded border border-white/15 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider text-white/70 hover:border-[#1a3fd1] hover:text-white">
                    <Copy className="h-3 w-3" /> copy
                  </button>
                </td>
              </tr>
            ))}
            {data.employees.length === 0 && (
              <tr><td colSpan={BOXES.length + 4} className="px-3 py-6 text-center font-mono text-[11px] text-white/50">Nobody was paid in {year}.</td></tr>
            )}
          </tbody>
          <tfoot>
            <tr className="border-t border-white/20 font-bold text-white" data-testid="t4-total-row">
              <td className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest" colSpan={3}>T4 Summary totals</td>
              {BOXES.map(([k]) => <td key={k} className="px-2 py-2 text-right font-mono tabular-nums">{money(t[k])}</td>)}
              <td />
            </tr>
          </tfoot>
        </table>
      </div>
      {cpp2For && <MissingCpp2Dialog employee={cpp2For} year={year} onClose={() => { setCpp2For(null); load(); }} />}
    </div>
  );
}
